import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'
import { pageTitle, sectionTitle, pageWide } from '@/lib/form-styles'
import { FeldStatus, FeldStatusZeichen, useAenderung, useStellenDaten, type Speicherstelle } from '@/lib/speichern'
import { useZeitKonfiguration } from '@/lib/zeit-konfiguration'
import { FieldOptionsEditor, aendereFeld, listeAus } from '../components/CustomFieldsManager'
import type { CustomFieldSchema } from '../types/time-tracking.types'
import { ArrowRight } from 'lucide-react'

/**
 * „Auswahlwerte eigener Felder" — zentrale Pflege der Dropdown-Werte aller
 * Auswahl-Felder (Typ A Zeiterfassung + Typ B Lohnverrechnung). Befüllt nur die
 * Werte (options[]); die Felddefinition selbst läuft über „Eigene Felder".
 * Gruppierte Auswahl-Felder beziehen ihre Werte aus „Zusatzfeld-Gruppen".
 *
 * AP-36 Phase C: Jeder Wert ist eine eigene Operation auf dem aktuellen Stand
 * (hinzu / weg), erkannt am Feldnamen. Früher ging bei jedem Klick die ganze
 * Liste hinaus, und zwei gleichzeitige Pflegen überschrieben einander.
 * Zugriff: ADMIN+ (ProtectedRoute in App.tsx).
 */
export default function FieldOptionsPage() {
  const { t } = useTranslation()
  const { stelle, geladen, fehler } = useZeitKonfiguration()
  if (fehler) {
    return (
      <div className={pageWide}>
        <div className="rounded-md border border-destructive/30 bg-destructive/10 px-4 py-3 text-sm text-destructive">{fehler}</div>
      </div>
    )
  }
  if (!geladen) return <div className="p-6 text-sm text-muted-foreground">{t('common.loading')}</div>
  return <Auswahlwerte stelle={stelle as Speicherstelle<unknown>} />
}

function Auswahlwerte({ stelle }: { stelle: Speicherstelle<unknown> }) {
  const daten = useStellenDaten(stelle)
  const selectsA = listeAus(daten, 'timeEntryCustomFieldsSchema').filter((f) => f.type === 'select')
  const selectsB = listeAus(daten, 'customFieldsSchema').filter((f) => f.type === 'select')
  const empty = selectsA.length === 0 && selectsB.length === 0

  return (
    <div className={pageWide}>
      <div>
        <h1 className={pageTitle}>Auswahlwerte eigener Felder</h1>
        <p className="mt-2 text-sm text-muted-foreground">
          Die Auswahlwerte aller Dropdown-Felder zentral an einer Stelle. Du kannst sie auch direkt am Feld unter „Eigene Felder" pflegen. Jeder Wert wird sofort gespeichert.
        </p>
      </div>

      {empty ? (
        <div className="rounded-lg border border-dashed border-border px-4 py-8 text-center">
          <p className="text-sm text-muted-foreground">Noch keine Auswahl-Felder vorhanden.</p>
          <p className="mt-1 text-xs text-muted-foreground">Lege unter „Eigene Felder" ein Feld vom Typ „Auswahl (Dropdown)" an, dann erscheinen hier seine Werte.</p>
        </div>
      ) : (
        <>
          {selectsA.length > 0 && (
            <section className="space-y-3">
              <h2 className={sectionTitle}>Zeiterfassung – Felder</h2>
              {selectsA.map((f) => (
                <FieldOptionsCard key={f.name} stelle={stelle} schluessel="timeEntryCustomFieldsSchema" field={f} />
              ))}
            </section>
          )}
          {selectsB.length > 0 && (
            <section className="space-y-3">
              <h2 className={sectionTitle}>Lohnverrechnung – Felder</h2>
              {selectsB.map((f) => (
                <FieldOptionsCard key={f.name} stelle={stelle} schluessel="customFieldsSchema" field={f} />
              ))}
            </section>
          )}
        </>
      )}
    </div>
  )
}

function FieldOptionsCard({ stelle, schluessel, field }: { stelle: Speicherstelle<unknown>; schluessel: string; field: CustomFieldSchema }) {
  const aenderung = useAenderung(stelle, `${schluessel}:${field.name}`)
  const grouped = !!field.group
  const label = { [schluessel]: field.label }
  return (
    <div className="rounded-lg border border-border bg-background p-4">
      <div className="flex flex-wrap items-center gap-2">
        <span className="font-medium text-foreground">{field.label}</span>
        <span className="inline-block rounded border border-border bg-muted px-2 py-0.5 text-xs font-medium text-foreground">Auswahl</span>
        {field.required && (
          <span className="inline-block rounded bg-destructive/15 px-2 py-0.5 text-xs font-medium text-destructive">Pflichtfeld</span>
        )}
        {grouped && (
          <span className="inline-block rounded border border-border bg-muted px-2 py-0.5 text-xs text-muted-foreground">Gruppe: {field.group}</span>
        )}
        <FeldStatusZeichen status={aenderung.status} />
      </div>
      <p className="mt-1 text-xs text-muted-foreground">
        Interner Name: <code className="font-mono">{field.name}</code>
      </p>

      {grouped ? (
        <div className="mt-3 flex flex-wrap items-center gap-2 rounded-md border border-dashed border-border px-3 py-2 text-sm text-muted-foreground">
          Werte stammen aus der Gruppe „{field.group}" — bearbeiten unter
          <Link to="/time-tracking/lists/groups" className="inline-flex items-center gap-1 text-primary hover:underline">
            Zusatzfeld-Gruppen <ArrowRight className="h-3.5 w-3.5" />
          </Link>
        </div>
      ) : (
        <FieldOptionsEditor
          options={field.options ?? []}
          onAdd={(wert) =>
            void aenderung.sende({
              felder: [schluessel],
              labels: label,
              operation: aendereFeld(schluessel, field.name, (f) => ({
                ...f,
                options: (f.options ?? []).includes(wert) ? f.options : [...(f.options ?? []), wert],
              })),
            })
          }
          onRemove={(wert) =>
            void aenderung.sende({
              felder: [schluessel],
              labels: label,
              operation: aendereFeld(schluessel, field.name, (f) => ({ ...f, options: (f.options ?? []).filter((o) => o !== wert) })),
            })
          }
        />
      )}
      <FeldStatus status={aenderung.status} zeichen={false} />
    </div>
  )
}
